import {
    FC,
    ReactElement,
    useMemo,
    useState,
} from 'react'
import { ChevronProps, DayPicker as ReactDayPicker } from 'react-day-picker'
import { de, enGB, nl } from 'react-day-picker/locale'

import { PayrollPeriod } from '@/entities/PayrollPeriod/PayrollPeriod'
import { getLocale, Locale } from '@/helpers/trans'
import { getDaysFromTimeMode } from '@/redux/@interface/calendar/calendarHelpers'
import { TimeModeType } from '@/types'

import './DayPicker.scss'

interface DayPickerProps {
    payrollPeriods: PayrollPeriod[]
    selectedDays: Date[]
    timeMode: TimeModeType
    onDayClick: (day: Date) => void
    className?: string
}

const dayPickerLocales = {
    [Locale.nl]: nl,
    [Locale.en]: enGB,
    [Locale.de]: de,
}

const Chevron = ({ orientation }: ChevronProps): ReactElement => (
    <svg viewBox="0 0 12 16" className={`day-picker__chevron day-picker__chevron--${orientation}`} aria-hidden="true">
        <path d="M2 1 L11 8 L2 15 Z" />
    </svg>
)

export const DayPicker: FC<DayPickerProps> = ({
    payrollPeriods,
    selectedDays,
    timeMode,
    onDayClick,
    className = '',
}): ReactElement => {
    const [hoverDate, setHoverDate] = useState<Date>()

    const hoverRange = useMemo(() => {
        if (! hoverDate) {
            return []
        }

        return getDaysFromTimeMode(timeMode, hoverDate, payrollPeriods)
    }, [hoverDate, payrollPeriods, timeMode])

    const handleDayMouseEnter = (day: Date): void => {
        setHoverDate(day)
    }

    const handleDayMouseLeave = (): void => {
        setHoverDate(undefined)
    }

    return (
        <ReactDayPicker
            mode="multiple"
            locale={dayPickerLocales[getLocale()]}
            weekStartsOn={1}
            defaultMonth={selectedDays[0]}
            selected={selectedDays}
            showWeekNumber
            showOutsideDays
            modifiers={{ hoverRange }}
            modifiersClassNames={{ hoverRange: 'day-picker__day--is-in-hover-range' }}
            components={{ Chevron }}
            onDayClick={onDayClick}
            onDayMouseEnter={handleDayMouseEnter}
            onDayMouseLeave={handleDayMouseLeave}
            className={className}
            classNames={{
                root: 'day-picker',
                months: 'day-picker__months',
                month: 'day-picker__month',
                month_caption: 'day-picker__caption',
                caption_label: 'day-picker__caption-label',
                nav: 'day-picker__nav',
                button_previous: 'day-picker__nav-button',
                button_next: 'day-picker__nav-button',
                month_grid: 'day-picker__grid',
                weekdays: 'day-picker__weekdays',
                weekday: 'day-picker__weekday',
                week_number_header: 'day-picker__week-number-header',
                weeks: 'day-picker__weeks',
                week: 'day-picker__week',
                week_number: 'day-picker__week-number',
                day: 'day-picker__day',
                day_button: 'day-picker__day-button',
                selected: 'day-picker__day--is-selected',
                today: 'day-picker__day--is-today',
                outside: 'day-picker__day--is-outside',
                disabled: 'day-picker__day--is-disabled',
                hidden: 'day-picker__day--is-hidden',
                focused: 'day-picker__day--is-focused',
            }}
        />
    )
}
